import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { create } from '../../actions/ad-subscription-namespaces';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import type { RootState } from '../../reducers';
import InputError from '../../styled-components/components/InputError';
import InputLabel from '../../styled-components/components/InputLabel';
import Loader from '../../styled-components/components/Loader';

const toRequestBody = (name: string) => ({
  name,
});

const AdSubscriptionNamespaceForm = () => {
  const { t } = useTranslation(['list', 'validation']);
  const dispatch = useDispatch();

  const requestStatus = useSelector<RootState, number>(
    (s) => s.subscriptions.namespaces.requestStatus,
  );
  const responseStatus = useSelector<RootState, number>(
    (s) => s.subscriptions.namespaces.responseStatus,
  );

  const actions = bindActionCreators(
    {
      create,
    },
    dispatch,
  );

  const [validation, setValidation] = useState<boolean | undefined>(undefined);
  const [listName, setListName] = useState('');

  const onCreate = () => {
    if (listName.length) {
      actions.create(toRequestBody(listName));
    } else {
      setValidation(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setListName(e.currentTarget.value);
    if (e.currentTarget.value.length) {
      setValidation(true);
    } else {
      setValidation(false);
    }
  };

  const { pending } = useDataExchangeStatus(ExchangeTypes.Fetching, [
    requestStatus,
    responseStatus,
  ]);
  const { successful, pending: loading } = useDataExchangeStatus(ExchangeTypes.Creating, [
    requestStatus,
    responseStatus,
  ]);

  useEffect(() => {
    if (successful) {
      setValidation(undefined);
      setListName('');
    }
  }, [successful]);

  const displayError = validation !== undefined && validation === false;

  if (pending) {
    return <Loader />;
  }

  return (
    <>
      <InputLabel htmlFor="input-create-new-list">{t('nameOfList', { ns: 'list' })}</InputLabel>
      <div data-wx-inputtext="action">
        <input
          type="text"
          value={listName}
          onChange={onChange}
          placeholder={t('nameOfListPlaceholder', { ns: 'list' })}
          id="input-create-new-list"
        />
        <div className="input-text-action">
          <button
            type="button"
            data-wx-button="action thin"
            onClick={onCreate}
            title={t('createList', { ns: 'list' })}
          >
            {t('createList', { ns: 'list' })}
            {loading && <Loader inlineBlock />}
          </button>
        </div>
        <div className="input-text-ghost" />
      </div>

      {displayError && <InputError>{t('fieldCanNotBeEmpty', { ns: 'validation' })}</InputError>}
    </>
  );
};

export default AdSubscriptionNamespaceForm;
